import { z } from 'zod';

import { TraceSpanSchema } from './trace-viewer';

export type { TraceSpan } from './trace-viewer';

export const ResultSchema = z.object({
  url: z.string(),
  title: z.string(),
  content: z.string(),
});

export const ContentSchema = z.object({
  type: z.string(),
  blurb: z.string(),
  results: z.array(ResultSchema),
  identifier: z.string(),
  search_query: z.string(),
});

export const PreviousFilesSchema = z.object({
  found_files: z.array(z.unknown()),
  not_found_file_keys: z.array(z.unknown()),
});

export const FoundContextContextSchema = z.object({
  type: z.string(),
  results: z.array(ResultSchema),
  search_query: z.string(),
});

export const FoundContextSchema = z.object({
  context: FoundContextContextSchema,
  identifier: z.string(),
});

export const PreviousModalitiesSchema = z.object({
  found_contexts: z.array(FoundContextSchema),
  not_found_identifiers: z.array(z.unknown()),
});

export const ContextElementSchema = z.object({
  type: z.string(),
  format: z.string().optional(),
  content: z.union([z.string(), z.array(ContentSchema)]).optional(),
  chat_history_idx: z.number(),
  previous_files: PreviousFilesSchema.optional(),
  previous_modalities: PreviousModalitiesSchema.optional(),
});

export const ToolCallSchema = z.object({
  index: z.string(),
  tool_id: z.string().nullable(),
  func_name: z.string(),
  func_args: z.string(),
});

export const ToolResultSchema = z.object({
  tool_id: z.string().nullable(),
  func_name: z.string(),
  result: z.string(),
});

export const UserMessageSchema = z.object({
  role: z.literal('user'),
  id: z.string().nullable(),
  created_at: z.string().nullable(),
  updated_at: z.string().nullable(),
  texts: z.array(z.string()),
  agent: z.string().nullable(),
  uploads: z.array(z.unknown()).nullable(),
  file_ids: z.array(z.string()).nullable(),
});
export type UserMessage = z.infer<typeof UserMessageSchema>;

export const AssistantMessageSchema = z.object({
  role: z.literal('assistant'),
  id: z.string(),
  created_at: z.string().nullable(),
  updated_at: z.string().nullable(),
  agent: z.string().nullable(),
  texts: z.array(z.string()),
  thought: z.string().nullable(),
  tool_use: z.array(ToolCallSchema).nullable(),
  invocations: z.array(z.unknown()).nullable(),
  sources: z.string().nullable(),
  file_ids: z.array(z.string()).nullable(),
  context: z.array(ContextElementSchema).nullable(),
  system_prompt: z.string().nullable(),
});
export type AssistantMessage = z.infer<typeof AssistantMessageSchema>;

export const ToolMessageSchema = z.object({
  role: z.literal('tool'),
  id: z.string().nullable(),
  created_at: z.string().nullable(),
  updated_at: z.string().nullable(),
  tool_results: z.array(ToolResultSchema),
});
export type ToolMessage = z.infer<typeof ToolMessageSchema>;

export const ChatMessageSchema = z.discriminatedUnion('role', [
  UserMessageSchema,
  AssistantMessageSchema,
  ToolMessageSchema,
]);
export type ChatMessage = z.infer<typeof ChatMessageSchema>;

export const ChatTurnTraceSchema = z.object({
  trace_id: z.string().nullable(),
  trace_url: z.string().nullable(),
  user_query: z.string().nullable(),
  messages: z.array(ChatMessageSchema),
});
export type ChatTurnTrace = z.infer<typeof ChatTurnTraceSchema>;

export const ChatDebugMessagesResponseSchema = z.object({
  chat_id: z.string(),
  chat_turn_traces: z.array(ChatTurnTraceSchema),
  source: z.string(),
});

export const TraceApiResponseSchema = z.object({
  trace_id: z.string(),
  tree: TraceSpanSchema,
});

export const CostBreakdownSchema = z.object({
  fresh_input_tokens: z.number(),
  cached_read_tokens: z.number(),
  cache_write_tokens: z.number(),
  output_tokens: z.number(),
  thought_tokens: z.number(),
  fresh_input_cost_usd: z.number(),
  cached_read_cost_usd: z.number(),
  cache_write_cost_usd: z.number(),
  output_cost_usd: z.number(),
  thought_cost_usd: z.number(),
  total_usd: z.number(),
});
export type CostBreakdown = z.infer<typeof CostBreakdownSchema>;

export const ZERO_COST_BREAKDOWN: CostBreakdown = {
  fresh_input_tokens: 0,
  cached_read_tokens: 0,
  cache_write_tokens: 0,
  output_tokens: 0,
  thought_tokens: 0,
  fresh_input_cost_usd: 0,
  cached_read_cost_usd: 0,
  cache_write_cost_usd: 0,
  output_cost_usd: 0,
  thought_cost_usd: 0,
  total_usd: 0,
};

export function sumCostBreakdowns(
  a: CostBreakdown,
  b: CostBreakdown
): CostBreakdown {
  return {
    fresh_input_tokens: a.fresh_input_tokens + b.fresh_input_tokens,
    cached_read_tokens: a.cached_read_tokens + b.cached_read_tokens,
    cache_write_tokens: a.cache_write_tokens + b.cache_write_tokens,
    output_tokens: a.output_tokens + b.output_tokens,
    thought_tokens: a.thought_tokens + b.thought_tokens,
    fresh_input_cost_usd: a.fresh_input_cost_usd + b.fresh_input_cost_usd,
    cached_read_cost_usd: a.cached_read_cost_usd + b.cached_read_cost_usd,
    cache_write_cost_usd: a.cache_write_cost_usd + b.cache_write_cost_usd,
    output_cost_usd: a.output_cost_usd + b.output_cost_usd,
    thought_cost_usd: a.thought_cost_usd + b.thought_cost_usd,
    total_usd: a.total_usd + b.total_usd,
  };
}

// thought_tokens are only a separately-billed pool when thought_cost_usd > 0 (e.g. Gemini).
// Claude/OpenAI already include thoughts in output_tokens, so adding them unconditionally
// would double-count. Mirrors the backend's billed_total_tokens.
export function sumBilledTokens(breakdown: CostBreakdown): number {
  return (
    breakdown.fresh_input_tokens +
    breakdown.cached_read_tokens +
    breakdown.cache_write_tokens +
    breakdown.output_tokens +
    (breakdown.thought_cost_usd > 0 ? breakdown.thought_tokens : 0)
  );
}

export const LlmUsageSchema = z.object({
  model: z.string(),
  input_tokens: z.number(),
  output_tokens: z.number(),
  cached_tokens: z.number(),
  cache_creation_tokens: z.number(),
  thought_tokens: z.number(),
});

export type ContextBreakdownKind =
  | 'system'
  | 'system_static'
  | 'system_dynamic'
  | 'system_field'
  | 'tools'
  | 'tool_definition'
  | 'conversation'
  | 'user_message'
  | 'assistant_message'
  | 'thoughts'
  | 'tool_call'
  | 'tool_result'
  | 'compaction_summary';

export type ContextBreakdownItem = {
  kind: ContextBreakdownKind;
  label: string;
  token_count: number;
  character_count?: number | null;
  content_hash?: string;
  children: ContextBreakdownItem[];
};

export const ContextBreakdownItemSchema: z.ZodType<ContextBreakdownItem> =
  z.lazy(() =>
    z.object({
      kind: z.enum([
        'system',
        'system_static',
        'system_dynamic',
        'system_field',
        'tools',
        'tool_definition',
        'conversation',
        'user_message',
        'assistant_message',
        'thoughts',
        'tool_call',
        'tool_result',
        'compaction_summary',
      ]),
      label: z.string(),
      token_count: z.number(),
      character_count: z.number().nullable().optional(),
      content_hash: z.string().default(''),
      children: z.array(ContextBreakdownItemSchema).default([]),
    })
  );

export const CompactionCallCostSchema = z.object({
  created_at: z.string(),
  model: z.string(),
  usage: LlmUsageSchema,
  cost: CostBreakdownSchema,
});
export type CompactionCallCost = z.infer<typeof CompactionCallCostSchema>;

export const SubagentStepCostSchema = z.object({
  message_id: z.string(),
  step_index: z.number().nullable(),
  model: z.string(),
  usage: LlmUsageSchema,
  cost: CostBreakdownSchema,
});

export const SubagentRunCostSchema = z.object({
  child_chat_id: z.string(),
  description: z.string(),
  agent: z.string(),
  created_at: z.string(),
  steps: z.array(SubagentStepCostSchema).default([]),
  compaction_calls: z.array(CompactionCallCostSchema).default([]),
  steps_total: CostBreakdownSchema,
  compaction_total: CostBreakdownSchema,
  total_cost: CostBreakdownSchema,
});
export type SubagentRunCost = z.infer<typeof SubagentRunCostSchema>;

export const TurnCostSummarySchema = z.object({
  turn_index: z.number(),
  message_id: z.string(),
  created_at: z.string(),
  model: z.string(),
  steps: z.array(LlmUsageSchema),
  step_costs: z.array(CostBreakdownSchema),
  total_cost: CostBreakdownSchema,
  cache_expired: z.boolean().nullable().optional(),
  turn_gap_ms: z.number().nullable().optional(),
  subagent_runs: z.array(SubagentRunCostSchema).default([]),
  subagent_total: CostBreakdownSchema.nullable().default(null),
});
export type TurnCostSummary = z.infer<typeof TurnCostSummarySchema>;

export const CostStepInfoSchema = z.object({
  chat_id: z.string(),
  message_id: z.string().nullable(),
  step_index: z.number().nullable(),
  model: z.string(),
  server_emitted_at: z.string(),
  fresh_input_cost_usd: z.number(),
  cached_read_cost_usd: z.number(),
  cache_write_cost_usd: z.number(),
  output_cost_usd: z.number(),
  thought_cost_usd: z.number(),
  total_usd: z.number(),
  fresh_input_tokens: z.number().default(0),
  cached_read_tokens: z.number().default(0),
  cache_write_tokens: z.number().default(0),
  total_prompt_tokens: z.number(),
  output_tokens: z.number(),
  thought_tokens: z.number(),
  context_window: z.number().nullable(),
  context_breakdown: z.array(ContextBreakdownItemSchema).default([]),
});
export type CostStepInfo = z.infer<typeof CostStepInfoSchema>;

export const ChatCostSummarySchema = z.object({
  chat_id: z.string(),
  title: z.string(),
  turns: z.array(TurnCostSummarySchema),
  total_tokens: z.number(),
  total_cost_usd: z.number(),
  compaction_calls: z.array(CompactionCallCostSchema),
  compaction_total: CostBreakdownSchema.nullable(),
  mid_turn_compaction_calls: z.array(CompactionCallCostSchema).default([]),
  mid_turn_compaction_total: CostBreakdownSchema.nullable().optional(),
  grand_total_usd: z.number().nullable(),
  latest_step: CostStepInfoSchema.nullable(),
});
export type ChatCostSummary = z.infer<typeof ChatCostSummarySchema>;

export const PayloadSectionSchema = z.object({
  index: z.number(),
  message_index: z.number().nullable().optional(),
  role: z.string().nullable().optional(),
  section_type: z.enum([
    'system_prompt_static',
    'system_prompt_dynamic',
    'tool_definition',
    'user_message',
    'assistant_message',
    'thoughts',
    'tool_call',
    'tool_result',
    'file',
    'image',
    'unknown',
  ]),
  label: z.string(),
  token_count: z.number(),
  character_count: z.number(),
  content_hash: z.string(),
  field_tokens: z.record(z.string(), z.number()).nullable().optional(),
  content: z.string().nullable().optional(),
});
export type PayloadSection = z.infer<typeof PayloadSectionSchema>;

export const DebugPayloadSkeletonSchema = z.object({
  message_id: z.string(),
  created_at: z.string(),
  step_index: z.number().nullable().optional(),
  agent_id: z.string().nullable().optional(),
  agent_config: z.record(z.string(), z.unknown()).nullable().optional(),
  model: z.string().nullable().optional(),
  sections: z.array(PayloadSectionSchema).default([]),
  cache_miss_reason: z.string().nullable().optional(),
});
export type DebugPayloadSkeleton = z.infer<typeof DebugPayloadSkeletonSchema>;

export const DebugFullPayloadSchema = z.object({
  model: z.string().nullable(),
  system_prompt: z.string().nullable(),
  tools: z.array(z.record(z.string(), z.unknown())).nullable(),
  message_chain: z.array(z.array(z.string())),
});
export type DebugFullPayload = z.infer<typeof DebugFullPayloadSchema>;

export const DebugPayloadSkeletonsResponseSchema = z.object({
  chat_id: z.string(),
  title: z.string(),
  payloads: z.array(DebugPayloadSkeletonSchema),
  debug_data_scrubbed: z.boolean().default(false),
  subagent_payloads: z
    .record(z.string(), z.array(DebugPayloadSkeletonSchema))
    .default({}),
});
export type DebugPayloadSkeletonsResponse = z.infer<
  typeof DebugPayloadSkeletonsResponseSchema
>;
